import { request } from './index'
import type {
  User,
  LoginParams,
  RegisterParams,
  LoginResponse,
  ChangePasswordParams,
  UpdateUserInfoParams,
  UserPageParams,
  UserStatistics,
  SocialAccountBindParams,
  UserProfileStatistics,
  AccessLog,
  AccessLogPageParams,
  AccessStatistics
} from '@/types/user'
import type { PageResponse } from './index'

export const userApi = {
  // 用户注册
  register(params: RegisterParams) {
    return request.post<User>('/user/register', params)
  },

  // 用户登录
  login(params: LoginParams) {
    return request.post<LoginResponse>('/user/login', params)
  },

  // 刷新Token
  refreshToken(refreshToken: string) {
    return request.post<LoginResponse>('/user/refresh-token', null, {
      params: { refreshToken }
    })
  },

  // 用户登出
  logout(userId: number, token: string) {
    return request.post('/user/logout', null, {
      params: { userId },
      headers: { Authorization: `Bearer ${token}` }
    })
  },

  // 获取用户信息
  getUserInfo(userId: number) {
    return request.get<User>(`/user/${userId}`)
  },

  // 获取用户列表
  getUserList(params?: { userType?: number; userStatus?: number; keyword?: string }) {
    return request.get<User[]>('/user/list', { params })
  },

  // 20260528 LZH 查询游客及用户访问会话列表，用于商家后台用户管理页统计访问量。
  getAccessLogs(params?: AccessLogPageParams) {
    return request.get<PageResponse<AccessLog>>('/user/access-logs', { params })
  },

  // 20260528 LZH 查询游客及用户访问统计，用于商家后台用户管理页统计访问量。
  getAccessStatistics() {
    return request.get<AccessStatistics>('/user/access-statistics')
  },

  // 更新用户信息
  updateUserInfo(userId: number, params: UpdateUserInfoParams) {
    return request.put(`/user/${userId}`, params)
  },

  // 修改密码(需先获取手机或邮箱验证码)
  changePassword(userId: number, oldPassword: string, newPassword: string, verifyChannel: 'PHONE' | 'EMAIL', verifyCode: string) {
    return request.put(`/user/${userId}/password`, null, {
      params: { oldPassword, newPassword, verifyChannel, verifyCode }
    })
  },

  // 发送账号安全验证码(手机/邮箱 + 绑定/修改密码)
  sendSecurityCode(userId: number, channel: 'PHONE' | 'EMAIL', scene: 'BIND' | 'CHANGE_PASSWORD') {
    return request.post(`/user/${userId}/security-code/send`, { channel, scene })
  },

  // 发送绑定/换绑邮箱验证码(用户在个人资料页输入新邮箱时调用)
  sendEmailBindCode(email: string) {
    return request.post('/user/email/send', { email })
  },

  // 用户自助绑定/换绑邮箱(必须本人操作 + 邮箱验证码)
  bindEmail(userId: number, email: string, emailCode: string) {
    return request.put(`/user/${userId}/email`, { email, emailCode })
  },

  // 重置密码
  resetPassword(userId: number, newPassword: string) {
    return request.put(`/user/${userId}/reset-password`, null, {
      params: { newPassword }
    })
  },

  // 验证当前邮箱(账号安全)
  verifyEmail(userId: number, code: string) {
    return request.put(`/user/${userId}/verify-email`, null, {
      params: { code }
    })
  },

  // 验证当前手机号(账号安全)
  verifyPhone(userId: number, code: string) {
    return request.put(`/user/${userId}/verify-phone`, null, {
      params: { code }
    })
  },

  // 发送新手机验证码（更换手机号第二步）
  sendNewPhoneCode(userId: number, newPhone: string) {
    return request.post(`/user/${userId}/phone/send-code`, { newPhone })
  },

  // 更换手机号（验证当前手机后调用）
  changePhone(userId: number, newPhone: string, code: string) {
    return request.put(`/user/${userId}/phone`, { newPhone, code })
  },

  // 启用用户
  enableUser(userId: number) {
    return request.put(`/user/${userId}/enable`)
  },

  // 禁用用户
  disableUser(userId: number) {
    return request.put(`/user/${userId}/disable`)
  },

  // 分页查询用户
  getUserPage(params: UserPageParams) {
    return request.get<PageResponse<User>>('/user/page', { params })
  },

  // 条件分页查询用户
  getUserPageWithCondition(params: UserPageParams) {
    return request.get<PageResponse<User>>('/user/page/condition', { params })
  },

  // 检查用户名
  checkUsername(username: string, excludeUserId?: number) {
    return request.get<boolean>('/user/check-username', {
      params: { username, excludeUserId }
    })
  },

  // 检查邮箱
  checkEmail(email: string, excludeUserId?: number) {
    return request.get<boolean>('/user/check-email', {
      params: { email, excludeUserId }
    })
  },

  // 检查手机号
  checkPhone(phone: string, excludeUserId?: number) {
    return request.get<boolean>('/user/check-phone', {
      params: { phone, excludeUserId }
    })
  },

  // 用户状态统计
  getUserStatisticsByStatus() {
    return request.get<UserStatistics[]>('/user/statistics/status')
  },

  // 用户类型统计
  getUserStatisticsByType() {
    return request.get<UserStatistics[]>('/user/statistics/type')
  },

  // 最近注册用户
  getRecentRegisteredUsers(days = 7, limit = 10) {
    return request.get<User[]>('/user/recent-registered', {
      params: { days, limit }
    })
  },

  // 活跃用户
  getActiveUsers(days = 7, limit = 10) {
    return request.get<User[]>('/user/active', {
      params: { days, limit }
    })
  },

  // 获取用户统计数据
  getUserProfileStatistics(userId: number) {
    return request.get<UserProfileStatistics>(`/user/${userId}/statistics`)
  },

  // 绑定微信账号
  bindWechat(userId: number, params: SocialAccountBindParams) {
    return request.post(`/user/${userId}/bind/wechat`, params)
  },

  // 解绑微信账号
  unbindWechat(userId: number) {
    return request.delete(`/user/${userId}/bind/wechat`)
  },

  // 绑定QQ账号
  bindQQ(userId: number, params: SocialAccountBindParams) {
    return request.post(`/user/${userId}/bind/qq`, params)
  },

  // 解绑QQ账号
  unbindQQ(userId: number) {
    return request.delete(`/user/${userId}/bind/qq`)
  },

  // 新增管理员
  addAdmin(params: { username: string; password: string; email?: string; phone?: string; nickname?: string; userStatus?: number }) {
    return request.post<User>('/user/admin', { ...params, passwordHash: params.password })
  },

  // 获取用户订单列表
  getUserOrders(userId: number) {
    return request.get<any[]>(`/user/${userId}/orders`)
  },

  // 获取用户分享数据
  getUserPromotions(userId: number) {
    return request.get<any>(`/user/${userId}/promotions`)
  },

  // 删除用户
  deleteUser(userId: number) {
    return request.delete(`/user/${userId}`)
  },

  // 用户自助注销账号（需验证码）
  deactivateAccount(userId: number, channel: 'PHONE' | 'EMAIL', code: string) {
    return request.delete(`/user/${userId}/account`, { data: { channel, code } })
  },

  // 新用户首次设置密码
  initPassword(userId: number, newPassword: string, confirmPassword: string) {
    return request.put(`/user/${userId}/init-password`, { newPassword, confirmPassword })
  }
}
